'use client'
import { useState, useRef, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react'
import { debounce } from 'lodash'
import { cn } from '../../helpers/utils.js'
import { withLayout } from '../../shared/Styles.js'
import Button from '../../components/button.js'
import { VideoModal } from '../VideoModal/index.js'
import { convertLexicalToHTML } from '@payloadcms/richtext-lexical/html'
import type { MediaPlayerBaseProps } from './types.js'
// import { Right } from './right.js'
import { useInView } from 'react-intersection-observer'
import type { HTMLConvertersFunction } from '@payloadcms/richtext-lexical/html'
import { useExposure } from '../../hooks/useExposure.js'
import ScrollLoadVideo from '../../helpers/ScrollLoadVideo.js'
import { gsap } from 'gsap'
import { SplitText } from 'gsap/dist/SplitText'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'

const componentName = 'media_player_base'
const componentType = 'video'

// 注册 gsap 插件
gsap.registerPlugin(ScrollTrigger, SplitText)

const htmlConverters: HTMLConvertersFunction = ({ defaultConverters }) => ({
  ...defaultConverters,
  text: args => {
    const { node } = args
    // 检查是否有自定义 color
    if (node.$ && node.$.color) {
      return `<span class="lexical-${node.$.color}">${node.text}</span>`
    }
    return node.text
  },
})

const MediaPlayerBase = forwardRef<HTMLDivElement, MediaPlayerBaseProps>(
  (
    {
      className = '',
      id,
      onBtnClick,
      data: {
        title,
        videoTitle,
        btnText,
        youtubeId,
        video,
        mobileVideo,
        theme,
        img,
        shape,
        titleAnimation = 'fade-in',
        ...dataRest
      },
      ...rest
    },
    ref
  ) => {
    const { sticky } = dataRest
    const [isPlaying, setIsPlaying] = useState(false)
    const [btb, setbtb] = useState(0)
    const [titleHeight, setTitleHeight] = useState(0)
    const [videoHeightProgress, setVideoHeightProgress] = useState(0)
    const [visible, setVisible] = useState<boolean>(false)

    const videoRef = useRef<HTMLVideoElement>(null)
    const bgRef = useRef<HTMLImageElement>(null)
    const titleRef = useRef<HTMLDivElement>(null)
    const titleFadeInRef = useRef<HTMLDivElement>(null)
    const trackRef = useRef<HTMLDivElement>(null)
    const splitTextInstance = useRef<SplitText | null>(null)
    const btnFadeInRef = useRef<HTMLButtonElement>(null)
    const scrollTriggerRef = useRef<ScrollTrigger | null>(null)
    const heightTriggerRef = useRef<ScrollTrigger | null>(null)
    const { ref: inViewRef, inView } = useInView()

    useImperativeHandle(ref, () => titleRef.current as HTMLDivElement)

    const title_html =
      typeof title === 'string' ? title : title && convertLexicalToHTML({ data: title, converters: htmlConverters })
    const videoTitle_html =
      typeof videoTitle === 'string'
        ? videoTitle
        : videoTitle && convertLexicalToHTML({ data: videoTitle, converters: htmlConverters })

    useEffect(() => {
      if (inView) {
        videoRef.current?.play()
        setIsPlaying(true)
      } else {
        videoRef.current?.pause()
        setIsPlaying(false)
      }
    }, [inView])

    const debouncedHandleResize = debounce(() => {
      if (titleRef.current) {
        const rect = titleRef.current.getBoundingClientRect()
        const screenHeight = window.innerHeight
        const scrollTop = window.scrollY || window.pageYOffset
        const elementBottomToPageTop = rect.bottom + scrollTop
        const pageHeight = document.documentElement.scrollHeight
        const distanceToPageBottom = pageHeight - elementBottomToPageTop
        setbtb(distanceToPageBottom > screenHeight ? screenHeight : distanceToPageBottom)
      }
      if (titleRef.current) {
        const titleHeight = titleRef.current.clientHeight
        const screenHeight = window.innerHeight
        setTitleHeight(titleHeight + screenHeight)
      }
    }, 600)

    useEffect(() => {
      debouncedHandleResize()
      window.addEventListener('resize', debouncedHandleResize)
      return () => {
        window.removeEventListener('resize', debouncedHandleResize)
      }
    }, [])

    useEffect(() => {
      function gsapResize() {
        if (!titleFadeInRef.current || titleAnimation !== 'fade-in') return
        const height = titleFadeInRef.current?.clientHeight || 80
        splitTextInstance.current = new SplitText(titleFadeInRef.current, {
          type: 'words',
          wordsClass: 'word',
        })
        const words = splitTextInstance.current.words
        gsap.set(words, { opacity: 0 })
        if (btnFadeInRef.current) gsap.set(btnFadeInRef.current, { opacity: 0 })
        scrollTriggerRef.current = ScrollTrigger.create({
          trigger: titleRef.current,
          start: 'top center-=10%',
          end: `top+=${height * 1.5 + 80}px center-=10%`,
          scrub: true,
          onUpdate: (self: any) => {
            const progress = self.progress
            const total = words.length
            const interval = 1 / total
            const overlap = 0.5
            words.forEach((word: any, i: number) => {
              const start = (i / total) * (1 - overlap)
              const width = interval * (1 + overlap)
              let opacity = (progress - start) / width
              opacity = Math.max(0, Math.min(opacity, 1))
              gsap.set(word, { opacity })
            })
            gsap.set(bgRef.current, { opacity: progress })
            if (btnFadeInRef.current) {
              gsap.set(btnFadeInRef.current, { opacity: progress >= 0.6 ? 1 : 0 })
            }
          },
        })
      }

      function gsapVideoHeightResize() {
        heightTriggerRef.current = ScrollTrigger.create({
          trigger: titleRef.current,
          start: 'bottom bottom',
          end: `bottom top`,
          // markers: true,
          scrub: true,
          onUpdate: (self: any) => {
            const progress = self.progress
            setVideoHeightProgress(progress)
          },
        })
      }

      if (inView) {
        gsapResize()
        gsapVideoHeightResize()
      }

      return () => {
        splitTextInstance.current && splitTextInstance.current.revert()
        // ScrollTrigger.getAll().forEach((t: { kill: () => any }) => t.kill())
        scrollTriggerRef.current && scrollTriggerRef.current.kill()
        heightTriggerRef.current && heightTriggerRef.current.kill()
      }
    }, [titleAnimation, inView])

    useExposure(trackRef, {
      componentType,
      componentName,
      componentTitle: title_html,
    })

    const zIndexVideo = useMemo(() => {
      if (inView && videoHeightProgress > 0 && videoHeightProgress < 0.9) return 3
      if (inView) return 2
      return 1
    }, [videoHeightProgress, inView])

    const aspect =
      'lg-desktop:aspect-w-[1920] lg-desktop:aspect-h-[930] desktop:aspect-w-[1024] desktop:aspect-h-[520] laptop:aspect-w-[1024] laptop:aspect-h-[520] tablet:aspect-w-[768] tablet:aspect-h-[660] aspect-w-[390] aspect-h-[660]'
    return (
      <>
        {sticky && (
          <div
            {...rest}
            ref={titleRef}
            className={cn(
              'pointer-events-none relative z-10',
              aspect,
              {
                'aiui-dark': theme === 'dark',
                'rounded-box': shape === 'rounded',
              },
              className
            )}
          >
            <div ref={inViewRef}>
              {!titleAnimation && (
                <div className="media-content absolute left-1/2 top-1/2 z-20 w-full -translate-x-1/2 -translate-y-1/2 px-4 text-center">
                  {title_html && !isPlaying && (
                    <div
                      className="lg-desktop:text-[64px] text-info-primary text-center text-[40px] font-bold leading-none lg:text-5xl"
                      dangerouslySetInnerHTML={{ __html: title_html }}
                    />
                  )}
                  {videoTitle_html && isPlaying && (
                    <div
                      className="lg-desktop:text-[64px] text-info-primary text-center text-[40px] font-bold leading-none lg:text-5xl"
                      dangerouslySetInnerHTML={{ __html: videoTitle_html }}
                    />
                  )}
                  {btnText && (
                    <Button
                      variant="link"
                      className={cn('member-equity-button-secondary text-info-primary')}
                      onClick={() => {
                        setVisible(true)
                        // if (isPlaying) {
                        //   if (videoRef.current) {
                        //     videoRef.current.pause()
                        //   }
                        //   setIsPlaying(false)
                        // } else {
                        //   if (videoRef.current) {
                        //     videoRef.current.play()
                        //   }
                        //   setIsPlaying(true)
                        // }
                        onBtnClick && onBtnClick?.()
                      }}
                    >
                      {btnText}
                      {/* <Right /> */}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        <div
          {...(!sticky ? rest : {})}
          style={
            sticky
              ? {
                  marginBottom: `-${btb}px`,
                  marginTop: `-${titleHeight}px`,
                  zIndex: zIndexVideo,
                }
              : { zIndex: 5 }
          }
          className={cn('relative', className)}
        >
          <div className="sticky top-0 ">
            <div
              id={id}
              className={cn('relative overflow-hidden', sticky ? 'h-screen w-full' : aspect, {
                'aiui-dark': theme === 'dark',
                'rounded-box': shape === 'rounded',
              })}
            >
              {titleAnimation === 'fade-in' && (
                <div
                  className={cn(
                    'absolute z-30 flex w-full flex-col items-center justify-center gap-4 px-4 text-center',
                    sticky ? 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2' : 'left-0 top-0'
                  )}
                >
                  {title_html && (
                    <div
                      ref={titleFadeInRef}
                      className="media-player-base-title lg-desktop:text-[64px] text-info-primary text-[40px] font-bold leading-none lg:text-5xl"
                      dangerouslySetInnerHTML={{ __html: title_html }}
                    />
                  )}
                  {btnText && (
                    <Button
                      ref={btnFadeInRef}
                      variant="link"
                      className={cn('media-player-base-button member-equity-button-secondary text-info-primary')}
                      onClick={() => {
                        setVisible(true)
                        onBtnClick && onBtnClick?.()
                      }}
                    >
                      {btnText}
                    </Button>
                  )}
                </div>
              )}
              <div
                className="media-cover left-0 top-0 h-screen w-screen overflow-hidden"
                style={{ height: `${102 - videoHeightProgress * 100}vh` }}
              >
                <ScrollLoadVideo
                  videoRef={videoRef}
                  poster={img?.url || ''}
                  src={video?.url!}
                  className="tablet:block hidden size-full !h-auto"
                  videoClassName="object-cover"
                  muted
                  loop
                  playsInline
                  autoplay
                  webkit-playsinline
                  x5-playsinline
                />
                <ScrollLoadVideo
                  videoRef={videoRef}
                  poster={img?.url || ''}
                  src={mobileVideo?.url || video?.url!}
                  className="tablet:hidden block min-h-screen w-full"
                  videoClassName="object-cover w-full h-auto"
                  muted
                  loop
                  playsInline
                  autoplay
                  webkit-playsinline
                  x5-playsinline
                />
                <div
                  ref={bgRef}
                  className="absolute left-0 top-0 z-10 size-full opacity-0"
                  style={{
                    background: 'rgba(0, 0, 0, 0.2)',
                  }}
                />
              </div>
            </div>
          </div>
          {sticky && (
            <div
              className={cn(sticky && 'pointer-events-none relative box-content block', aspect)}
              style={sticky ? { height: `${btb}px` } : {}}
              ref={trackRef}
            />
          )}
        </div>
        {visible && youtubeId && (
          <VideoModal visible={visible} youTubeId={youtubeId} onCloseModal={() => setVisible(false)} />
        )}
      </>
    )
  }
)

MediaPlayerBase.displayName = 'MediaPlayerBase'

export default withLayout(MediaPlayerBase)
